import { useState, useEffect } from "react";
import API from "@/lib/api";
import { Trophy, Crown, Medal } from "lucide-react";

export default function Leaderboard({ game: fixedGame }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setGame] = useState("asteroids");
  const game = fixedGame || tab;

  useEffect(() => {
    let alive = true;
    API.get("/leaderboard", { params: { game } })
      .then(({ data }) => alive && setEntries(data.leaderboard))
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [game]);

  const tabs = fixedGame ? null : (
    <div className="flex gap-2 mb-4" data-testid="leaderboard-tabs">
      {[["asteroids", "Asteroids"], ["invaders", "Space Invaders"]].map(([id, label]) => (
        <button
          key={id}
          onClick={() => setGame(id)}
          data-testid={`leaderboard-tab-${id}`}
          className={`px-3 py-1 rounded-full font-mono text-xs transition-colors duration-200 ${
            game === id ? "bg-cyber text-void font-bold" : "bg-white/5 text-white/50 hover:text-white"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="glass-surface rounded-xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Trophy className="w-5 h-5 text-cyber" />
          <h2 className="font-heading text-lg font-bold text-white">Leaderboard</h2>
        </div>
        {tabs}
        <p className="font-mono text-sm text-white/30 animate-pulse">Loading scores...</p>
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="glass-surface rounded-xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Trophy className="w-5 h-5 text-cyber" />
          <h2 className="font-heading text-lg font-bold text-white">Leaderboard</h2>
        </div>
        {tabs}
        <p className="font-body text-sm text-white/30" data-testid="leaderboard-empty">No scores yet. Be the first to play!</p>
      </div>
    );
  }

  const RankIcon = ({ rank }) => {
    if (rank === 1) return <Crown className="w-5 h-5 text-yellow-400" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-gray-300" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
    return <span className="font-mono text-sm text-white/30 w-5 text-center">{rank}</span>;
  };

  return (
    <div className="glass-surface rounded-xl p-6" data-testid="leaderboard">
      <div className="flex items-center gap-2 mb-5">
        <Trophy className="w-5 h-5 text-cyber" />
        <h2 className="font-heading text-lg font-bold text-white">Leaderboard</h2>
        <span className="font-mono text-xs text-white/30 ml-1">Top Scores</span>
      </div>

      {tabs}
      <div className="space-y-2">
        {entries.slice(0, 10).map((entry, i) => (
          <div
            key={entry.id}
            data-testid={`leaderboard-entry-${i}`}
            className={`flex items-center gap-3 p-3 rounded-lg transition-colors duration-200 ${
              entry.is_you
                ? "bg-cyber/10 border border-cyber/20"
                : "bg-white/[0.02] hover:bg-white/[0.04]"
            }`}
          >
            <RankIcon rank={i + 1} />
            <div className="flex-1 min-w-0">
              <p className={`font-body text-sm truncate ${entry.is_you ? "text-cyber font-medium" : "text-white"}`}>
                {entry.student_name}
                {entry.is_you && <span className="text-xs text-cyber/60 ml-1">(you)</span>}
              </p>
            </div>
            <span className={`font-mono text-xs px-1.5 py-0.5 rounded ${
              entry.mode === "bonus" ? "bg-cyber/10 text-cyber" : "bg-white/5 text-white/30"
            }`}>
              {entry.mode === "bonus" ? "BONUS" : "STD"}
            </span>
            <span className="font-mono text-lg font-bold text-white tabular-nums w-20 text-right">
              {entry.score.toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
